import { useState, useEffect, useCallback } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { ChevronIcon } from '../../../assets/icons';
import Button from '../../../components/Button';
import Card from '../../../components/Card';
import Dropdown from '../../../components/Dropdown';
import SideBySideTokens from '../../../components/SideBySideTokens/SideBySideTokens';
import useChartState from '../../../context/chart/useChartState';
import useTradePools from '../../../hooks/web3/trade/useTradePools';
import ExchangeMarketList from './ExchangeMarketList';
import MarketPrice from '../ExchangeHeader/MarketPrice';
import useWidth from '../../../hooks/useWidth';
import { useWeb3React } from '@web3-react/core';
import { useParams } from 'react-router-dom';
import {
    chartSymbolToRouteParam,
    formatPercentages,
    verifyChartSymbol,
} from '../../../helpers';

type ExchangeMarketOptionsProps = {
    collapsed: boolean;
    setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

const ExchangeMarketOptions: React.FC<ExchangeMarketOptionsProps> = ({
    collapsed,
    setCollapsed,
}) => {
    const { chainId, library } = useWeb3React();
    const { tradePools, mutate } = useTradePools();
    const { selectedTradePair, favouriteMarkets, setSelectedTradePair } = useChartState();

    const windowWidth = useWidth();
    const { chartSymbol } = useParams();

    const [previousChain, setPreviousChain] = useState(chainId);

    const toggleDropdown = () => setCollapsed((prev) => !prev);

    const closeDropdown = useCallback(() => setCollapsed(false), [setCollapsed]);

    const updatePoolOnUrlParamChange = useCallback(() => {
        if (!tradePools || !library) return;

        const chartSymbolParam = chartSymbol
            ? chartSymbolToRouteParam(chartSymbol, true)
            : undefined;

        const isValidSymbol = verifyChartSymbol(chartSymbolParam);

        if (!selectedTradePair) {
            const pool = isValidSymbol
                ? tradePools.find(
                      ({ chartSymbol: poolChartSymbol }) =>
                          poolChartSymbol === chartSymbolParam
                  )
                : undefined;

            const sortedPools = tradePools
                .map((pool) => ({
                    ...pool,
                    isFavourited: favouriteMarkets.includes(pool.name),
                }))
                .sort(
                    // sorts by favourite then, sorts by name
                    (a, b) =>
                        (a.isFavourited === b.isFavourited
                            ? 0
                            : a.isFavourited
                            ? -1
                            : 1) || a.name.localeCompare(b.name)
                );

            // if unknown URL param, set to default pool
            setSelectedTradePair(pool ?? sortedPools[0]);
            return;
        }
    }, [
        library,
        chartSymbol,
        tradePools,
        favouriteMarkets,
        selectedTradePair,
        setSelectedTradePair,
    ]);

    const updatePoolOnChainChange = useCallback(
        (chainId: number | undefined) => {
            if (!chainId || !previousChain || !selectedTradePair) return;

            if (chainId !== previousChain) {
                setPreviousChain(chainId);
                // update selected pool when chainId changes
                (async () => {
                    try {
                        const newTradePools = await mutate();

                        if (!newTradePools) return;

                        const pool = newTradePools.find(
                            ({ name }) => name === selectedTradePair.name
                        );

                        if (!pool) return;

                        setSelectedTradePair(pool);
                    } catch (error) {
                        console.log(error);
                    }
                })();
            }
        },
        [previousChain, selectedTradePair, setSelectedTradePair, mutate]
    );

    // inital setup of previous chain, runs once only
    useEffect(() => {
        if (!previousChain && chainId) {
            setPreviousChain(chainId);
        }
    }, [chainId, previousChain]);

    useEffect(() => {
        closeDropdown();
    }, [selectedTradePair, closeDropdown]);

    // initial setup of pool based on URL param
    useEffect(() => {
        updatePoolOnUrlParamChange();
    }, [chainId, updatePoolOnUrlParamChange]);

    // updates selected pool data when chain changes
    useEffect(() => {
        updatePoolOnChainChange(chainId);
    }, [chainId, updatePoolOnChainChange]);

    return (
        <div className='exchange-market-options'>
            <ClickAwayListener onClickAway={closeDropdown}>
                <Card onClick={windowWidth > 900 ? undefined : toggleDropdown}>
                    <div className='exchange-market-option select-custom'>
                        <Button
                            className='selected-label'
                            onClick={windowWidth > 900 ? toggleDropdown : undefined}
                            tabIndex={windowWidth > 900 ? -1 : 0}
                        >
                            {selectedTradePair ? (
                                <>
                                    {' '}
                                    <SideBySideTokens tokens={selectedTradePair.icons} />
                                    <span>
                                        {selectedTradePair.name.replaceAll(' ', '')}
                                    </span>
                                    <div className='max-leverage'>
                                        {formatPercentages(
                                            selectedTradePair.maxLeverage,
                                            0
                                        )}
                                        x
                                    </div>
                                </>
                            ) : (
                                '-'
                            )}
                            <ChevronIcon />
                        </Button>
                        <Dropdown open={collapsed}>
                            <ExchangeMarketList />
                        </Dropdown>
                    </div>
                    <MarketPrice pool={selectedTradePair} />
                </Card>
            </ClickAwayListener>
        </div>
    );
};

export default ExchangeMarketOptions;
